<i18n lang="yaml">
  pt:
    pageDescription: 'Junte-se ao time {workspaceName}! Confira nossas vagas de emprego disponíveis.'
  en:
    pageDescription: 'Join the {workspaceName} team! Check out our available job openings.'
</i18n>

<template>
  <div class="h-full">
    <TheLandscape
      v-if="showView"
      :view="view"
      :fields="columns"
      :privacy="privacy"
      :search="search"
      :current-group-by-field="recordToolboxOptions.hasGroup ? recordToolboxOptions.currentGroupByField : null"
      :loading="loading"
      fit-content
      comfort-read-mode
      page-state-component-key="index"
      class="h-full"
      @data-change="isViewType('Tree') && refresh()"
    />

    <LogoLoading v-else />
  </div>
</template>

<script lang="ts">
import LogoLoading from '~/components/LogoLoading.vue';
import { mapMutations, mapGetters, mapState } from '~/assets/javascript/modules/vuex';
import ROUTES from '~/assets/javascript/constants/routes';

export default {
  name: 'PublicViews',
  components: {
    TheLandscape: defineAsyncComponent(() => import('~/components/views/the-landscape/index.vue')),
    LogoLoading,
  },
  async setup() {
    const { t } = useI18n();

    definePageMeta({
      layout: 'public',
      isPublic: true,
    });

    const store = {
      ...mapState('view', ['view']) as { view: ComputedRef<View> },
      // eslint-disable-next-line vue/no-unused-properties
      ...mapGetters('workspace', ['workspaceName']),
      ...mapGetters('recordList', ['recordToolboxOptions']),
      ...mapMutations('table', ['initTable']),
      ...mapMutations('viewsMenu', ['resetFilterToolbarSearch']),
    };

    const {
      refresh,
      status: fetchStatus,
      data: {
        columns,
      },
    } = await useAsyncData(async () => {
      const { $router, $localeRoute } = useNuxtApp();
      const { viewLoadError, columns } = await useViewFetch({ isPublic: true, skipBuildHead: true, autoRecordRedirect: true });

      if (viewLoadError) {
        navigateTo($localeRoute({ name: ROUTES.public.views.routeName, params: { tenantSlug: $router.currentRoute.value.params.tenantSlug } }));
      }

      useAnalyticsPage('public-view-show', { name: store.view.value.page_name }, { immediate: true });

      return { columns };
    });

    const workspaceName = store.workspaceName?.value;

    buildHead({
      titleTemplate: `%s - ${workspaceName}`,
      title: computed(() => store.view.value?.page_name),
      // TODO: eventually we need to stop hardcoding ATS stuff for public pages. This should be customizable by the user!
      description: t('pageDescription', { workspaceName }),
    });

    useTrackAppUsage('index');

    return {
      ...store,
      columns,
      fetchStatus,
      refresh,
    };
  },
  data() {
    return {
      search: '',
    };
  },
  computed: {
    showView() {
      return Boolean(this.view) && this.view.id === this.$route.params.viewId;
    },
    loading() {
      return this.fetchStatus === 'pending';
    },
    privacy() {
      return this.view.sheet?.privacy;
    },
  },
  mounted() {
    this.resetFilterToolbarSearch();
    this.initTable();
  },
  methods: {
    isViewType(type) {
      return this.view.page_type === type;
    },
  },
};
</script>
